import styled, { css, keyframes } from 'styled-components';

// Sits under the sticky Back header rather than over it, so "Back to Path"
// stays reachable without touching BuddyDialog.
export const Stage = styled.div(
  ({ theme }) => css`
    position: fixed;
    inset: 0;
    z-index: ${theme.zi.z1};
    background-color: ${theme.co.secondary0};
    overflow: hidden;
  `,
);

export const Video = styled.video(
  () => css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Clips are framed for portrait, so a wide viewport crops top and bottom.
       Favour the upper part of the shot to keep faces out of the crop. */
    object-position: center 25%;
  `,
);

// Words with no filmed speaker get the session's mosaic as a backdrop and the
// recording front and centre, matching the intro and end-of-session screens.
export const AudioOverlay = styled.div(
  () => css`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;
  `,
);

export const AudioCard = styled.div(
  ({ theme }) => css`
    width: min(90%, ${theme.sz.lg});
    display: flex;
    align-items: center;
    gap: ${theme.sz.s6};
    padding: ${theme.sz.s5} ${theme.sz.s8} ${theme.sz.s5} ${theme.sz.s5};
    border-radius: ${theme.bo.mediumLarge};
    background-color: ${theme.co.neutral99};
    box-shadow: 0 ${theme.sz.s1} ${theme.sz.s6} rgb(0 17 34 / 25%);
    pointer-events: auto;

    @media (max-width: ${theme.sz.md}) {
      gap: ${theme.sz.s4};
      padding: ${theme.sz.s4};
    }
  `,
);

export const PlayButton = styled.button(
  ({ theme }) => css`
    flex: none;
    width: ${theme.sz.s20};
    height: ${theme.sz.s20};
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary20};
    color: ${theme.co.secondary0};
    font-size: ${theme.sz.s8};
    box-shadow: 0 ${theme.sz.s1} ${theme.sz.s3} rgb(0 17 34 / 30%);
    cursor: pointer;

    svg {
      font-size: 1em;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @media (max-width: ${theme.sz.md}) {
      width: ${theme.sz.s14};
      height: ${theme.sz.s14};
      font-size: ${theme.sz.s6};
    }
  `,
);

export const AudioWaveform = styled.div(
  ({ theme }) => css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.sz.s1};
    height: ${theme.sz.s10};
  `,
);

export const AudioWaveformBar = styled.div<{
  $scale: number;
  $index: number;
  $isPlaying: boolean;
}>(
  ({ theme, $scale, $index, $isPlaying }) => css`
    flex: 1;
    height: ${$scale * 100}%;
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.tertiary80};

    ${
      $isPlaying &&
      css`
        animation: ${pulse} 0.9s ease-in-out infinite;
        animation-delay: ${$index * 40}ms;

        @media (prefers-reduced-motion: reduce) {
          animation: none;
        }
      `
    };
  `,
);

const scrim = css`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

export const TopScrim = styled.div(
  ({ theme }) => css`
    ${scrim};
    top: 0;
    flex-direction: column;
    gap: ${theme.sz.s2};
    /* The sticky Back header is ${theme.sz.s16} tall, so clear it plus room to breathe */
    padding: ${theme.sz.s24} ${theme.sz.s8} ${theme.sz.s12};
    background: linear-gradient(
      to bottom,
      rgb(0 17 34 / 85%) 0%,
      rgb(0 17 34 / 60%) 55%,
      rgb(0 17 34 / 0%) 100%
    );

    @media (max-width: ${theme.sz.md}) {
      padding: ${theme.sz.s16} ${theme.sz.s4} ${theme.sz.s8};
    }
  `,
);

export const Eyebrow = styled.div(
  ({ theme }) => css`
    color: ${theme.co.primary20};
    letter-spacing: 0.1em;
    text-transform: uppercase;
    text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
    ${theme.typography.heading.mobile.xxxs};
  `,
);

export const TargetValue = styled.div(
  ({ theme }) => css`
    color: ${theme.co.neutral99};
    text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
    ${theme.typography.heading.desktop.m};

    @media (max-width: ${theme.sz.md}) {
      ${theme.typography.heading.mobile.m};
    }

    /* The word is the point of the screen, so give it the room a large tablet
       or desktop viewport can spare. */
    @media (min-width: ${theme.sz.lg}) {
      ${theme.typography.heading.desktop.l};
    }

    @media (min-width: ${theme.sz.xxl}) {
      ${theme.typography.heading.desktop.xl};
    }
  `,
);

export const Phonetic = styled.div(
  ({ theme }) => css`
    color: ${theme.co.neutral99};
    text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
    ${theme.typography.paragraph.base};

    @media (min-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.l};
    }
  `,
);

export const SourceValue = styled.div(
  ({ theme }) => css`
    color: ${theme.co.neutral99};
    opacity: 0.85;
    text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
    ${theme.typography.paragraph.base};

    @media (min-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.l};
    }
  `,
);

export const BottomScrim = styled.div(
  ({ theme }) => css`
    ${scrim};
    bottom: 0;
    padding: ${theme.sz.s12} ${theme.sz.s8} ${theme.sz.s8};
    background: linear-gradient(to top, rgb(0 17 34 / 75%), rgb(0 17 34 / 0%));

    @media (max-width: ${theme.sz.md}) {
      padding: ${theme.sz.s8} ${theme.sz.s4} ${theme.sz.s6};
    }
  `,
);

export const RecordCluster = styled.div(
  ({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.sz.s4};
  `,
);

export const RecordRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s4};
  `,
);

export const MicrophoneButton = styled.button<{ $isRecording: boolean }>(
  ({ theme, $isRecording }) => css`
    flex: none;
    width: ${theme.sz.s24};
    height: ${theme.sz.s24};
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    border-radius: ${theme.bo.round};
    background-color: ${$isRecording ? theme.co.error50 : theme.co.primary20};
    color: ${$isRecording ? theme.co.neutral99 : theme.co.secondary0};
    font-size: ${theme.sz.s12};
    box-shadow: 0 ${theme.sz.s1} ${theme.sz.s4} rgb(0 17 34 / 35%);
    cursor: pointer;

    svg {
      font-size: 1em;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
);

export const RecordCopy = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.sz.s2};
  `,
);

export const MicrophoneHint = styled.div(
  ({ theme }) => css`
    color: ${theme.co.neutral99};
    text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
    ${theme.typography.heading.desktop.s};
  `,
);

const pulse = keyframes`
  0%, 100% { transform: scaleY(0.3); }
  50% { transform: scaleY(1); }
`;

export const Waveform = styled.div<{ $isRecording: boolean }>(
  ({ theme, $isRecording }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s1};
    height: ${theme.sz.s6};
    opacity: ${$isRecording ? 1 : 0.4};
  `,
);

export const WaveformBar = styled.div<{ $index: number; $isRecording: boolean }>(
  ({ theme, $index, $isRecording }) => css`
    width: ${theme.sz.s1};
    height: 100%;
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.success70};
    transform: scaleY(0.3);

    ${
      $isRecording &&
      css`
        animation: ${pulse} 0.9s ease-in-out infinite;
        animation-delay: ${$index * 60}ms;
      `
    };
  `,
);

export const SecondaryActions = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.sz.s3};
  `,
);

export const SpeakerCount = styled.div(
  ({ theme }) => css`
    color: ${theme.co.neutral99};
    text-shadow: 0 1px 3px rgb(0 17 34 / 60%);
    ${theme.typography.heading.desktop.xxs};
  `,
);

export const RoundButton = styled.button(
  ({ theme }) => css`
    width: ${theme.sz.s12};
    height: ${theme.sz.s12};
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.neutral99};
    color: ${theme.co.tertiary0};
    font-size: ${theme.sz.s5};
    cursor: pointer;

    svg {
      font-size: 1em;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `,
);

// Takes the microphone's place while the attempt is analysed and scored, so
// the speaker stays on screen and the controls never move around it.
export const ResultCard = styled.div(
  ({ theme }) => css`
    width: min(100%, ${theme.sz.xs});
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};
    padding: ${theme.sz.s5};
    border-radius: ${theme.bo.mediumLarge};
    background-color: ${theme.co.neutral99};
    box-shadow: 0 ${theme.sz.s1} ${theme.sz.s6} rgb(0 17 34 / 25%);

    @media (max-width: ${theme.sz.md}) {
      width: 100%;
    }
  `,
);

export const ResultHeading = styled.div<{ $isPass: boolean }>(
  ({ theme, $isPass }) => css`
    color: ${$isPass ? theme.co.success20 : theme.co.secondary0};
    ${theme.typography.heading.desktop.s};
  `,
);

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

// The scoring round trip has no progress to report, so the dots carry the
// "still working" signal on their own.
export const Ellipsis = styled.span(
  () => css`
    display: inline-flex;
  `,
);

export const Dot = styled.span<{ $index: number }>(
  ({ $index }) => css`
    animation: ${blink} 1.4s ease-in-out infinite;
    animation-delay: ${$index * 200}ms;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      opacity: 1;
    }
  `,
);

export const ResultActions = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s3};
  `,
);

const cta = css`
  flex: 1;
  padding: ${({ theme }) => `${theme.sz.s3} ${theme.sz.s4}`};
  border-radius: ${({ theme }) => theme.bo.round};
  cursor: pointer;
  ${({ theme }) => theme.typography.heading.desktop.xxs};
`;

export const SecondaryCta = styled.button(
  ({ theme }) => css`
    ${cta};
    border: ${theme.bo.thinWidth} solid ${theme.co.secondary80};
    background-color: ${theme.co.neutral99};
    color: ${theme.co.secondary0};
  `,
);

export const PrimaryCta = styled.button(
  ({ theme }) => css`
    ${cta};
    border: none;
    background-color: ${theme.co.primary20};
    color: ${theme.co.secondary0};
  `,
);
